import { Suspense, useEffect, useRef } from "react";
import { lazyRetry as lazy } from "@/lib/lazy-retry";
import { ensureOwnershipClaimsLocaleLoaded } from "@/i18n/loadOwnershipClaimsLocale";
import { ensureEmailAiLocaleLoaded } from "@/i18n/loadEmailAiLocale";
import { ensureDocumentScannerLocaleLoaded } from "@/i18n/loadDocumentScannerLocale";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";

/**
 * Forwards any /dashboard/<subpath> URL to /dash/<subpath>, preserving the
 * full path + query string + hash. Previously the catch-all redirect
 * dropped the subpath, which surfaced as a deep-link bounce to the
 * dispatcher home for any legacy /dashboard/X URL whose /dash/X
 * counterpart actually exists.
 */
function DashboardPathForwarder() {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/dashboard(\/|$)/, "/dash$1");
  return <Navigate to={newPath + location.search + location.hash} replace />;
}

/**
 * /auth → /login alias that preserves the query string. Team-invite emails
 * link to /auth?team_invite=<token>; a bare <Navigate to="/login"> would
 * drop the token before Login.tsx can stash it (src/lib/team-invite.ts).
 */
function AuthAliasForwarder() {
  const location = useLocation();
  return <Navigate to={"/login" + location.search + location.hash} replace />;
}

/**
 * /v/:reportNo — short public alias for the canonical report verify page.
 * Printed reports keep the canonical /verify/report/… URL (a long-lived PDF
 * should never depend on an alias); this catches hand-typed / space-tight
 * shares. Preserves the ?t= verification token untouched.
 */
function VerifyShortRedirect() {
  const { reportNo } = useParams<{ reportNo: string }>();
  const location = useLocation();
  return <Navigate to={`/verify/report/${reportNo ?? ""}${location.search}`} replace />;
}

/**
 * /dash/scans/:scanId → /dash/packages/scan/:scanId. The scan API's
 * dashboard_url and the escalation reviewer email both link to /dash/scans/…,
 * which had no route and 404'd. Canonical page stays under /dash/packages.
 */
function ScanShortRedirect() {
  const { scanId } = useParams<{ scanId: string }>();
  const location = useLocation();
  return <Navigate to={`/dash/packages/scan/${scanId ?? ""}${location.search}`} replace />;
}

/**
 * /dashboard/billing → /dash/settings/billing preserving the query string.
 * Stripe checkout returns to /dashboard/billing?checkout=success&tier=…; a
 * bare <Navigate> dropped the params, so the success banner never rendered.
 */
function BillingAliasForwarder() {
  const location = useLocation();
  return <Navigate to={"/dash/settings/billing" + location.search + location.hash} replace />;
}

/**
 * Back-compat redirect for the retired /dash/admin/hub/* routes. The admin hub
 * was lifted up to BE /dash/admin, so any saved deep link under the old
 * /dash/admin/hub prefix (e.g. /dash/admin/hub/growth, /dash/admin/hub/tenant/:id)
 * forwards to the same subpath under /dash/admin. A bare /dash/admin/hub maps
 * to /dash/admin. Preserves query string + hash.
 */
function AdminHubLegacyRedirect() {
  const params = useParams();
  const location = useLocation();
  const rest = params["*"] ? `/${params["*"]}` : "";
  return <Navigate to={`/dash/admin${rest}` + location.search + location.hash} replace />;
}
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { CompanyModeProvider } from "@/hooks/useCompanyMode";
import { VerticalProvider } from "@/hooks/useVertical";
import { GlobalErrorProvider } from "@/components/GlobalErrorProvider";
import { CapHitModalProvider } from "@/components/CapHitModalProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import ScrollToTop from "@/components/ScrollToTop";
import { CinematicTransitionProvider } from "@/components/landing/CinematicTransitionProvider";
import { CinematicRouteFrame } from "@/components/landing/CinematicRouteFrame";
import { CinematicTransitionLayer } from "@/components/landing/CinematicTransitionLayer";
import { DashRouteTracer } from "@/components/dash-v2/DashRouteTracer";
import { MetaPixelRouteTracker } from "@/components/MetaPixelRouteTracker";
import { RouteProgressBar } from "@/components/dash-v2/RouteProgressBar";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PhaseGate } from "@/components/PhaseGate";
import { RoleGate } from "@/components/RoleGate";
import { OrgAdminGate } from "@/components/OrgAdminGate";
import { AuditModeRedirect } from "@/components/AuditModeRedirect";
import { RequireLogisticsRole } from "@/components/RequireLogisticsRole";
import { AdminRouteBoundary } from "@/components/admin/AdminRouteBoundary";
import { registryFeatures } from "@/lib/feature-flags";
import { CabIdGate } from "@/components/CabIdGate";
import { QuebecLanguageBanner } from "@/components/QuebecLanguageBanner";
import { CookieConsent } from "@/components/CookieConsent";
import { LocaleAutoDetect } from "@/components/LocaleAutoDetect";
import { SiteThemeProvider } from "@/hooks/useSiteTheme";

import {
  // Eager
  Index, Login, Signup, SignupRolePicker, NotFound, AuthCallback,
  // Team invite (dedicated account-creation page)
  TeamInviteAcceptPage,
  // Homepage v2 preview (temporary · /preview-home)
  HomeV2,
  // Homepage conversion rebuild (temporary · /homepage2)
  Homepage2,
  // Auth
  MfaVerify, ForgotPassword, ResetPassword, VerifyEmail,
  // Public
  DocsPage, AboutPage, PricingPage, CheckoutPage, SwitchingFromCarrier411Page, VsAlternativesPage, CoveragePage, AgentSignupPage, ConnectPage,
  ClaimAgentPage, CarrierOnboarding, StateRegistryPage,
  DemoVideoPage, DashboardDemoPage, IntegrationGuidePage, QuickstartPage, ProductPage,
  BlogIndex, BlogArticle, PublicCarrierLookup,
  // CR Registry · Wave 2.7 (home) + Wave 2.8 (detail/SEO)
  RegistryHomePage, RegistryDetailPage, RegistrySeoPage,
  UnsubscribePage,
  // Public trust link + carrier profile
  TrustLinkDriverPage, ClaimVerifyPage, TrustLinkReceiptPage, VerifyReportPage, SharedReportPage,
  // Legal
  SecurityPage, TermsPage, PrivacyPage, ApiTermsPage, CookiePolicyPage,
  // Trust
  StatusPage,
  // Mobile
  MobileLayout, MobileLoadFeed, MobileProfile, MobileAlerts,
} from "@/routes";

// Dash v2 (in progress) · mounts under /dash during the rebuild window.
// All dash pages are lazy-loaded so landing-page visitors don't pay for
// dashboard JS on first load. This dramatically improves mobile FCP and
// frees the main thread to respond to touch scrolling immediately.
const DashV2Layout = lazy(() => import("@/layouts/DashV2Layout"));
const SystemStatePage = lazy(() => import("@/pages/dash-v2/SystemStatePage"));
const DispatcherHomePage = lazy(() => import("@/pages/dash-v2/DispatcherHomePage"));
const DispatcherOnboardingPage = lazy(() => import("@/pages/dash-v2/DispatcherOnboardingPage"));
const CarrierLookupPage = lazy(() => import("@/pages/dash-v2/CarrierLookupPage"));
const VerificationDashboardPage = lazy(() => import("@/pages/dash-v2/VerificationDashboardPage"));
const ScoringMethodologyPage = lazy(() => import("@/pages/docs/ScoringMethodologyPage"));
// PUBLIC methodology (founder override 2026-08-18): the same component as
// /dash/docs/scoring, wrapped in the marketing shell. See the header of
// TrustScorePublicPage for why the weight table is published rather than gated.
const TrustScorePublicPage = lazy(() => import("@/pages/TrustScorePublicPage"));
const TrustLinksPage = lazy(() => import("@/pages/dash-v2/TrustLinksPage"));
const CarrierSignupPage = lazy(() => import("@/pages/dash-v2/CarrierSignupPage"));
const BrokerSignupPage = lazy(() => import("@/pages/dash-v2/BrokerSignupPage"));
const BrokerOnboardingPage = lazy(() => import("@/pages/dash-v2/BrokerOnboardingPage"));
const CarrierOnboardingPage = lazy(() => import("@/pages/dash-v2/CarrierOnboardingPage"));
const HomeGate = lazy(() => import("@/pages/dash-v2/HomeGate"));
const TrialEndedPage = lazy(() => import("@/pages/dash-v2/TrialEndedPage"));
const CarrierInviteLandingPage = lazy(() => import("@/pages/dash-v2/CarrierInviteLandingPage"));
const FollowUpsPage = lazy(() => import("@/pages/dash-v2/FollowUpsPage"));
const TrustLinkDriverPageV2 = lazy(() => import("@/pages/dash-v2/TrustLinkDriverPage"));
const TrustLinkPreviewPage = lazy(() => import("@/pages/dash-v2/TrustLinkPreviewPage"));
const CarrierInvitePreviewPage = lazy(() => import("@/pages/dash-v2/CarrierInvitePreviewPage"));
const DocumentsPage = lazy(() =>
  Promise.all([import("@/pages/dash-v2/DocumentsPage"), ensureDocumentScannerLocaleLoaded()]).then(([mod]) => mod),
);
const DocumentReportPage = lazy(() => import("@/pages/dash-v2/DocumentReportPage"));
const SandboxSettingsPage = lazy(() => import("@/pages/dash-v2/SandboxSettingsPage"));
const BillingPageV2 = lazy(() => import("@/pages/dash-v2/BillingPage"));
const ReferralsPage = lazy(() => import("@/pages/dash-v2/ReferralsPage"));
const FleetTrackingPage = lazy(() => import("@/pages/dash-v2/FleetTrackingPage"));
const DispatcherLoadBoardPage = lazy(() => import("@/pages/dash-v2/DispatcherLoadBoardPage"));
const TeamPageV2 = lazy(() => import("@/pages/dash-v2/TeamPage"));
const AdminRoadmapPage = lazy(() => import("@/pages/dash-v2/AdminRoadmapPage"));
const QACommandCenterPage = lazy(() => import("@/pages/dash-v2/admin/QACommandCenterPage"));
const TrustSafetyAdminPage = lazy(() => import("@/pages/dash-v2/admin/TrustSafetyAdminPage"));
const StatusAdminPage = lazy(() => import("@/pages/dash-v2/admin/StatusAdminPage"));
const AdminEldLookupPage = lazy(() => import("@/pages/dash-v2/admin/AdminEldLookupPage"));
const AdminWebhooksPage = lazy(() => import("@/pages/dash-v2/admin/AdminWebhooksPage"));
const FeedbackInboxPage = lazy(() => import("@/pages/dash-v2/admin/FeedbackInboxPage"));
const AdminReferralsPage = lazy(() => import("@/pages/dash-v2/admin/AdminReferralsPage"));
const AdminEmergencyControlsPage = lazy(() => import("@/pages/dash-v2/admin/AdminEmergencyControlsPage"));
const IntakeMonitorPage = lazy(() => import("@/pages/dash-v2/admin/IntakeMonitorPage"));
const PipelinesPage = lazy(() => import("@/pages/dash-v2/admin/PipelinesPage"));
const EmbedBadgeTesterPage = lazy(() => import("@/pages/dash-v2/admin/EmbedBadgeTesterPage"));
// DEV-only scanner results harness. Gating the lazy() behind import.meta.env.DEV
// (statically false in prod) lets rollup drop the dynamic import entirely, so the
// preview chunk never ships — not even as unreachable dead code.
const ScannerPreviewPage = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/ScannerPreviewPage"))
  : null;
// DEV-only print layout for the demo report PDFs (scripts/make-demo-report-pdfs.mjs).
const DemoReportPrintPage = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/DemoReportPrintPage"))
  : null;
// DEV-only print layout for the demo PACKET PDFs (scripts/make-demo-packet-pdfs.mjs).
const DemoPacketPrintPage = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/DemoPacketPrintPage"))
  : null;
// Standalone simulated Packet Scanner demo (fictional data, zero backend).
// PUBLIC since 2026-07-26 (founder flip; landing-nav CTA). No auth, no backend.
const ScannerDemoPage = lazy(() => import("@/pages/demo/ScannerDemoPage"));
const EmailAITesterPage = lazy(() =>
  Promise.all([import("@/pages/dash-v2/admin/EmailAITesterPage"), ensureEmailAiLocaleLoaded()]).then(([mod]) => mod),
);
const EmailAIObserverPage = lazy(() =>
  Promise.all([import("@/pages/dash-v2/EmailAIObserverPage"), ensureEmailAiLocaleLoaded()]).then(([mod]) => mod),
);
const DocReviewQueuePage = lazy(() => import("@/pages/dash-v2/admin/DocReviewQueuePage"));
const CABrokerClaimsPage = lazy(() => import("@/pages/dash-v2/admin/CABrokerClaimsPage"));
const DocBundlePage = lazy(() => import("@/pages/dash-v2/admin/DocBundlePage"));
const AdminBrokerPackageOpsPage = lazy(() => import("@/pages/dash-v2/admin/AdminBrokerPackageOpsPage"));
// AdminIngestHealthPage retired 2026-07-11 (route redirects to /dash/admin/health).
const AdminPackageScannerV2Page = lazy(() => import("@/pages/dash-v2/admin/AdminPackageScannerV2Page"));
// Admin hub (rebuilt cohesive admin: cockpit + section/detail/settings pages)
const AdminCockpitHubPage = lazy(() => import("@/pages/dash-v2/admin/hub/CockpitPage"));
const AdminHubGrowthPage = lazy(() => import("@/pages/dash-v2/admin/hub/GrowthPage"));
const AdminHubRevenuePage = lazy(() => import("@/pages/dash-v2/admin/hub/RevenuePage"));
const AdminHubVerificationPage = lazy(() => import("@/pages/dash-v2/admin/hub/VerificationPage"));
const AdminHubGeographyPage = lazy(() => import("@/pages/dash-v2/admin/hub/GeographyPage"));
const AdminHubHealthPage = lazy(() => import("@/pages/dash-v2/admin/hub/HealthPage"));
const AdminHubTenantsPage = lazy(() => import("@/pages/dash-v2/admin/hub/TenantsPage"));
const AdminHubTenantDetailPage = lazy(() => import("@/pages/dash-v2/admin/hub/TenantDetailPage"));
const AdminHubSettingsPage = lazy(() => import("@/pages/dash-v2/admin/hub/SettingsPage"));
// AdminTractionPage retired 2026-07-11 (route redirects to /dash/admin/growth).
const FounderBriefingPage = lazy(() => import("@/pages/dash-v2/admin/FounderBriefingPage"));
// AdminVCPage retired 2026-07-11 (route redirects to /dash/admin/revenue).
// AdminAcceleratorPage retired 2026-07-11 (route redirects to /dash/admin/growth).
const QACockpitPage = lazy(() => import("@/pages/dash-v2/admin/qa-cockpit/QACockpitPage"));
const CarrierDetailPage = lazy(() => import("@/pages/dash-v2/CarrierDetailPage"));
const MyTrustScorePage = lazy(() =>
  Promise.all([import("@/pages/dash-v2/MyTrustScorePage"), ensureOwnershipClaimsLocaleLoaded()]).then(([mod]) => mod),
);
const VerifyOwnershipEmailPage = lazy(() =>
  Promise.all([import("@/pages/dash-v2/VerifyOwnershipEmailPage"), ensureOwnershipClaimsLocaleLoaded()]).then(([mod]) => mod),
);
const BrokerLookupPage = lazy(() => import("@/pages/dash-v2/BrokerLookupPage"));
const BrokerDetailPage = lazy(() => import("@/pages/dash-v2/BrokerDetailPage"));
const CABrokerDetailPage = lazy(() => import("@/pages/dash-v2/CABrokerDetailPage"));
const CABrokerClaimVerifyPage = lazy(() => import("@/pages/dash-v2/CABrokerClaimVerifyPage"));
const CABrokerListPage = lazy(() => import("@/pages/dash-v2/CABrokerListPage"));
const ClaimCABPage = lazy(() => import("@/pages/dash-v2/ClaimCABPage"));
const PackageScannerLanding = lazy(() => import("@/pages/PackageScannerPage"));
const PackageVerifyPage = lazy(() => import("@/pages/PackageVerifyPage"));
const CABrokerDocumentsPage = lazy(() => import("@/pages/dash-v2/CABrokerDocumentsPage"));
const CABrokerReferPage = lazy(() => import("@/pages/refer/CABrokerReferPage"));
const PackageInboxPage = lazy(() => import("@/pages/dash-v2/PackageInboxPage"));
const PackageScannerPage = lazy(() => import("@/pages/dash-v2/PackageScannerPage"));
const ComplianceVaultPage = lazy(() => import("@/pages/dash-v2/vault/ComplianceVaultPage"));
const ScanDetailPage = lazy(() => import("@/pages/dash-v2/ScanDetailPage"));
const ReviewQueuePage = lazy(() => import("@/pages/dash-v2/ReviewQueuePage"));
const VaultRecordPage = lazy(() => import("@/pages/dash-v2/vault/VaultRecordPage"));
const PackageBuilderWizard = lazy(() => import("@/pages/dash-v2/PackageBuilderWizard"));
const PublicPackageView = lazy(() => import("@/pages/PublicPackageView"));
const BrokerPackagePage = lazy(() => import("@/pages/dash-v2/BrokerPackagePage"));
const MyPartnersPage = lazy(() => import("@/pages/dash-v2/MyPartnersPage"));
const PartnerAlertsPage = lazy(() => import("@/pages/dash-v2/PartnerAlertsPage"));
const DevelopersPage = lazy(() => import("@/pages/dash-v2/DevelopersPage"));
const UsagePageV2 = lazy(() => import("@/pages/dash-v2/UsagePage"));
const McpAuthDocsPage = lazy(() => import("@/pages/docs/McpAuthDocsPage"));
const MCPPage = lazy(() => import("@/pages/MCPPage"));
const MCPSetupPage = lazy(() => import("@/pages/MCPSetupPage"));
const BulkVerifyPage = lazy(() => import("@/pages/dash-v2/BulkVerifyPage"));
const SettingsHubPageV2 = lazy(() => import("@/pages/dash-v2/SettingsHubPage"));
const NotificationSettingsPage = lazy(() =>
  Promise.all([import("@/pages/dash-v2/NotificationSettingsPage"), ensureOwnershipClaimsLocaleLoaded()]).then(([mod]) => mod),
);
const ScannerSettingsPage = lazy(() => import("@/pages/dash-v2/ScannerSettingsPage"));
const ScanApiSettingsPage = lazy(() => import("@/pages/dash-v2/ScanApiSettingsPage"));
const FeedbackPage = lazy(() => import("@/pages/dash-v2/FeedbackPage"));
const EmailSettingsPage = lazy(() => import("@/pages/dash-v2/EmailSettingsPage"));
const VerificationSettingsPage = lazy(() => import("@/pages/dash-v2/VerificationSettingsPage"));
const LocaleSettingsPage = lazy(() => import("@/pages/dash-v2/LocaleSettingsPage"));
const DocumentSettingsPage = lazy(() => import("@/pages/dash-v2/DocumentSettingsPage"));
const DealSettingsPage = lazy(() => import("@/pages/dash-v2/DealSettingsPage"));
const TrustSettingsPage = lazy(() => import("@/pages/dash-v2/TrustSettingsPage"));
const SecuritySettingsPage = lazy(() => import("@/pages/dash-v2/SecuritySettingsPage"));
const SignAnchorSettingsPage = lazy(() => import("@/pages/dash-v2/SignAnchorSettingsPage"));
const RateSettingsPage = lazy(() => import("@/pages/dash-v2/RateSettingsPage"));
const IntegrationSettingsPage = lazy(() => import("@/pages/dash-v2/IntegrationSettingsPage"));
const McpConnectionsPage = lazy(() => import("@/pages/dash-v2/McpConnectionsPage"));
const OAuthConsentPage = lazy(() => import("@/pages/oauth/OAuthConsentPage"));
const EmailSequencesPage = lazy(() => import("@/pages/dash-v2/EmailSequencesPage"));
const EmailTemplatesPage = lazy(() => import("@/pages/dash-v2/EmailTemplatesPage"));
const EmailAIPage = lazy(() =>
  Promise.all([import("@/pages/dash-v2/EmailAIPage"), ensureEmailAiLocaleLoaded()]).then(([mod]) => mod),
);
const EmailScannerPage = lazy(() => import("@/pages/dash-v2/email/tools/ScannerPage"));
const EmailSchedulePage = lazy(() => import("@/pages/dash-v2/email/tools/SchedulePage"));
const SentPage = lazy(() => import("@/pages/dash-v2/email/SentPage"));
const KnowledgeBasePage = lazy(() => import("@/pages/dash-v2/email/KnowledgeBasePage"));
const EmailPlaybooksPage = lazy(() => import("@/pages/dash-v2/email/tools/PlaybooksPage"));
const EmailAuditPage = lazy(() => import("@/pages/dash-v2/email/tools/AuditPage"));
const EmailIntegrationsPage = lazy(() => import("@/pages/dash-v2/email/tools/IntegrationsPage"));
const AutomationRulesPage = lazy(() => import("@/pages/dash-v2/AutomationRulesPage"));
const InvoicesPageV2 = lazy(() => import("@/pages/dash-v2/InvoicesPage"));
const InvoiceSettingsPage = lazy(() => import("@/pages/dash-v2/InvoiceSettingsPage"));
const CarrierVettingSwipePage = lazy(() => import("@/pages/dash-v2/verify/CarrierVettingSwipePage"));
const FocusedAlertPage = lazy(() => import("@/pages/dash-v2/FocusedAlertPage"));
const ScoreDisputesPage = lazy(() => import("@/pages/dash-v2/ScoreDisputesPage"));
const SendPackagePage = lazy(() => import("@/pages/dash-v2/SendPackagePage"));
const LoadTrustTimelinePage = lazy(() => import("@/pages/dash-v2/loads/LoadTrustTimelinePage"));

/**
 * PageFallback · Used by Suspense during lazy-route chunk loads.
 *
 * UX rule: a route swap should NEVER flash an empty/white page. With
 * `v7_startTransition` enabled, React keeps the previous route mounted
 * while the next chunk loads, so we render a transparent fallback that
 * sits over the existing page. The top RouteProgressBar provides the
 * actual visual feedback. If a chunk takes longer than ~250ms, a soft
 * dark veil + spinner fades in so the user knows work is happening.
 */
const PageFallback = () => (
  <div
    className="fixed inset-0 z-[90] flex items-center justify-center bg-background/70 backdrop-blur-sm pointer-events-none opacity-0"
    style={{ animation: "fadeInDelayed 0.3s ease-out 0.25s forwards" }}
    aria-hidden="true"
  >
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  </div>
);

/**
 * /logout — dedicated sign-out route. Hand-typed /logout used to just bounce
 * through the auth guard back into the dashboard (no route existed). On mount
 * it runs the AuthProvider signOut (which also clears the query cache, scanner
 * resume markers, PostHog + Sentry identity) and lands on /login. Sign-out is
 * best-effort: even if the Supabase call rejects, the user still ends on the
 * login page. The ref guards StrictMode's double-mount from firing it twice.
 */
function LogoutRoute() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    void signOut()
      .catch(() => {})
      .finally(() => navigate("/login", { replace: true }));
  }, [signOut, navigate]);
  return <PageFallback />;
}

/**
 * VerifyFallback · Route-level Suspense fallback for the public /verify page.
 *
 * /verify is the paid-traffic destination and is lazy-loaded; on a COLD direct
 * hit there is no previous route behind the global PageFallback, so visitors
 * saw a bare near-black viewport with one spinner for seconds while the chunk
 * + heavy deps parsed (QA 2026-07-10 Bug 2). This paints the page's actual
 * shape immediately instead: header band, hero lines, and the search-card
 * outline, all pulsing.
 */
const VerifyFallback = () => (
  <div className="min-h-screen bg-background" aria-hidden="true">
    <div className="h-16 border-b border-white/[0.04]" />
    <div className="max-w-3xl mx-auto px-4 pt-20 animate-pulse">
      <div className="h-6 w-44 rounded-full bg-white/[0.05] mx-auto mb-8" />
      <div className="h-10 w-3/4 rounded-lg bg-white/[0.07] mx-auto mb-3" />
      <div className="h-10 w-1/2 rounded-lg bg-white/[0.07] mx-auto mb-10" />
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8">
        <div className="h-9 w-56 rounded-lg bg-white/[0.05] mb-6" />
        <div className="h-12 w-full rounded-lg bg-white/[0.05] mb-4" />
        <div className="h-12 w-full rounded-lg bg-white/[0.04]" />
      </div>
    </div>
  </div>
);

const App = () => (
  <HelmetProvider>
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <GlobalErrorProvider>
    <AuthProvider>
      <CompanyModeProvider>
        <VerticalProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <SiteThemeProvider>
              <CinematicTransitionProvider>
              <ScrollToTop />
              {/* IP-geo language auto-detect. One network call per session,
                  no UI. Sets i18n language to hi for IN visitors, ur for PK,
                  es for LATAM, etc. when the user hasn't already picked. */}
              <LocaleAutoDetect />
              {/* Quebec / French banner. Renders nothing for non-Quebec visitors
                  and for users who have already chosen a language. Mounted
                  above RouteProgressBar so its z-index sits over the nav. */}
              <QuebecLanguageBanner />
              {/* Opt-in cookie consent (Quebec Law 25 / PIPEDA). Holds PostHog
                  analytics dark until the visitor accepts. Renders nothing once
                  a choice is stored. */}
              <CookieConsent />
              {/* App-wide usage cap-hit modal. Listens for the cap-hit event a
                  denied action (429 / quota_exceeded) dispatches and opens one
                  shared upgrade modal. Inside BrowserRouter + AuthProvider so it
                  can navigate and resolve the tenant's next tier. */}
              <CapHitModalProvider />
              {/* Top progress bar on every route (public + dash) so navigation
                  always feels responsive even before the next chunk paints. */}
              <RouteProgressBar />
              {/* Mounted at App level so /dash/onboarding (and any future
                  top-level dash route that bypasses DashV2Layout) still
                  gets dash.section / dash.route tags + breadcrumbs. */}
              <DashRouteTracer />
              {/* Fires a Meta Pixel PageView on every client-side route change.
                  No-ops until the visitor accepts cookie consent (the pixel base
                  code is injected only by initMetaPixel after opt-in), so nothing
                  reaches Meta before consent. */}
              <MetaPixelRouteTracker />
              <CinematicRouteFrame>
                <Suspense fallback={<PageFallback />}>
                  <Routes>
                  {/* Public */}
                  <Route path="/" element={<Index />} />
                  {/* Homepage previews (HomeV2, Homepage2) are dev-only: both
                      still render placeholder blocks ("[Phase 1 placeholder]")
                      and were publicly loadable in prod for anyone who guessed
                      the path. Review them locally; swap into / when approved.
                      See src/pages/HomeV2.tsx and src/pages/Homepage2.tsx. */}
                  {import.meta.env.DEV && <Route path="/preview-home" element={<HomeV2 />} />}
                  {import.meta.env.DEV && <Route path="/homepage2" element={<Homepage2 />} />}
                  <Route path="/docs" element={<DocsPage />} />
                  <Route path="/docs/mcp/auth" element={<McpAuthDocsPage />} />
                  <Route path="/quickstart" element={<QuickstartPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  {/* Public methodology. /trust-score is the canonical URL;
                      /docs/scoring is kept as an alias because that is the path
                      the in-app "Full methodology" button and existing deep
                      links (#carrier-us, #broker-us, ...) already use, and a
                      shared link should not 404 for a signed-out reader. */}
                  <Route path="/trust-score" element={<TrustScorePublicPage />} />
                  <Route path="/docs/scoring" element={<TrustScorePublicPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/switching-from-carrier411" element={<SwitchingFromCarrier411Page />} />
                  <Route path="/vs" element={<VsAlternativesPage />} />
                  <Route path="/coverage" element={<CoveragePage />} />
                  {/* Retired 2026-07-01: duplicated /mcp/setup content, some of it stale
                      (referenced a non-existent SDK package and a load-search tool that
                      doesn't exist among the 8 Phase-1 tools). /mcp/setup is canonical. */}
                  <Route path="/developers" element={<Navigate to="/mcp/setup" replace />} />
                  <Route path="/mcp" element={<MCPPage />} />
                  <Route path="/mcp/setup" element={<MCPSetupPage />} />
                  <Route path="/agents/signup" element={<AgentSignupPage />} />
                  <Route path="/carriers" element={<CarrierOnboarding />} />
                  <Route path="/dispatchers" element={<Navigate to="/signup" replace />} />
                  {/* Non-freight onboarding routes hidden · feature-flagged */}
                  <Route path="/connect" element={<ConnectPage />} />
                  <Route path="/claim" element={<ClaimAgentPage />} />
                  <Route path="/claim/:agentId" element={<ClaimAgentPage />} />
                  <Route path="/demo" element={<DashboardDemoPage />} />
                  {/* The AI-to-AI "Scale demo" (/demo/scale) was DELETED
                      2026-07-26. It was Lovable scaffold from the initial
                      commit, pre-dating the verification-product pivot, and
                      claimed live agent activity its CORS-blocked data source
                      could never show. Founder call: remove, not fix. */}
                  <Route path="/demo/scale" element={<Navigate to="/product" replace />} />
                  {/* Simulated Packet Scanner demo — PUBLIC since 2026-07-26
                      (founder flip; header CTA on the landing nav). The page
                      needs no auth and touches no backend; the scanner_demo
                      flag now only gates the dash sidebar's "Scanner Demo"
                      entry. */}
                  <Route
                    path="/demo/scanner"
                    element={
                      <Suspense fallback={<PageFallback />}>
                        <ScannerDemoPage />
                      </Suspense>
                    }
                  />
                  <Route path="/demo-tour" element={<DemoVideoPage />} />
                  <Route path="/integrate" element={<IntegrationGuidePage />} />
                  {/* OAuth 2.1 consent screen — invoked by MCP clients (Claude Desktop,
                      Cursor, Claude Code) when they need user authorization. Path is
                      registered in Supabase Studio → Auth → OAuth Server → Authorization URL. */}
                  <Route path="/oauth/consent" element={<OAuthConsentPage />} />
                  <Route path="/blog" element={<BlogIndex />} />
                  <Route path="/blog/:slug" element={<BlogArticle />} />
                  {/* Demo calls were retired — /book-demo now forwards to sign-up
                      so any stale link/ad still lands somewhere useful. */}
                  <Route path="/book-demo" element={<Navigate to="/signup" replace />} />
                  {/* DEV-only: Package Scanner v2 results harness (no auth, mock
                      data). import.meta.env.DEV is false in production builds, so
                      the route is never registered there. */}
                  {import.meta.env.DEV && ScannerPreviewPage && (
                    <Route
                      path="/dev/scanner-preview"
                      element={
                        <Suspense fallback={<PageFallback />}>
                          <ScannerPreviewPage />
                        </Suspense>
                      }
                    />
                  )}
                  {/* DEV-only: the scanner demo WITHOUT the PhaseGate, so it can
                      be QA'd on a dev server with no login (the gated /demo/scanner
                      route needs an admin session while the flag is dark). */}
                  {import.meta.env.DEV && (
                    <Route
                      path="/dev/scanner-demo"
                      element={
                        <Suspense fallback={<PageFallback />}>
                          <ScannerDemoPage />
                        </Suspense>
                      }
                    />
                  )}
                  {/* DEV-only: print layout used to pre-render the demo report
                      PDFs (public/demo/reports) via scripts/make-demo-report-pdfs.mjs. */}
                  {import.meta.env.DEV && DemoReportPrintPage && (
                    <Route
                      path="/dev/demo-report-print"
                      element={
                        <Suspense fallback={<PageFallback />}>
                          <DemoReportPrintPage />
                        </Suspense>
                      }
                    />
                  )}
                  {/* DEV-only: print layout for the demo PACKET PDFs
                      (public/demo/packets) via scripts/make-demo-packet-pdfs.mjs. */}
                  {import.meta.env.DEV && DemoPacketPrintPage && (
                    <Route
                      path="/dev/demo-packet-print"
                      element={
                        <Suspense fallback={<PageFallback />}>
                          <DemoPacketPrintPage />
                        </Suspense>
                      }
                    />
                  )}
                  <Route
                    path="/verify"
                    element={
                      <Suspense fallback={<VerifyFallback />}>
                        <PublicCarrierLookup />
                      </Suspense>
                    }
                  />
                  {/* Public trust-report verification — the landing page behind
                      the verify link/QR on every issued PDF trust report. No
                      auth; token arrives as ?t= and is checked server-side. */}
                  <Route path="/verify/report/:reportNo" element={<Suspense fallback={<PageFallback />}><VerifyReportPage /></Suspense>} />
                  {/* Short alias for the same page; preserves ?t=. */}
                  <Route path="/v/:reportNo" element={<VerifyShortRedirect />} />
                  {/* Packet share recipient view — public, no auth; the share
                      token is the capability and is checked server-side. */}
                  <Route path="/share/report/:shareToken" element={<Suspense fallback={<PageFallback />}><SharedReportPage /></Suspense>} />

                  {/* CR Registry · public registry (Wave 2.7 + 2.8).
                      Feature-flagged: when `public_registry` is false the
                      route falls through to <NotFound /> via the catch-all
                      below. Three surfaces, ordered most-specific-first so
                      the SEO route wins over the detail route on a literal
                      `/registry/cr/...` path:
                        - /registry/cr/:cr_id/:slug → SEO page (gated by
                          registry_seo_pages — additional flag so we can
                          ship indexable URLs independently of the in-app
                          detail page).
                        - /registry/:cr_id → in-app detail page.
                        - /registry → home / search (Wave 2.7). */}
                  {registryFeatures.public_registry && registryFeatures.registry_seo_pages && (
                    <Route path="/registry/cr/:cr_id/:slug" element={<RegistrySeoPage />} />
                  )}
                  {registryFeatures.public_registry && (
                    <Route path="/registry/:cr_id" element={<RegistryDetailPage />} />
                  )}
                  {registryFeatures.public_registry && (
                    <Route path="/registry" element={<RegistryHomePage />} />
                  )}
                  <Route path="/product" element={<ProductPage />} />
                  <Route path="/product/packet-scanner" element={<Suspense fallback={<PageFallback />}><PackageScannerLanding /></Suspense>} />
                  {/* Renamed 2026-08-03: the product is the Packet Scanner, so
                      the URL says packet, not package. The old path stays as a
                      permanent redirect — it is live in shipped blog posts,
                      the sitemap-indexed article and outbound emails. */}
                  <Route path="/product/package-scanner" element={<Navigate to="/product/packet-scanner" replace />} />
                  <Route path="/p/:token" element={<Suspense fallback={<PageFallback />}><PackageVerifyPage /></Suspense>} />
                  <Route path="/unsubscribe" element={<UnsubscribePage />} />

                  {/* Public driver Trust Link page */}
                  <Route path="/track/preview" element={<TrustLinkPreviewPage />} />
                  <Route path="/tl/preview" element={<TrustLinkPreviewPage />} />
                  <Route path="/track/:token" element={<Suspense fallback={<PageFallback />}><TrustLinkDriverPage /></Suspense>} />
                  <Route path="/tl/:token" element={<Suspense fallback={<PageFallback />}><TrustLinkDriverPage /></Suspense>} />
                  {/* Public by-ID profile pages removed 2026-06-19: /carrier/:mc and
                      /broker/:mc exposed any entity's canonical trust score with no
                      login, no credits, and no rate limit (free-by-ID enumeration leak).
                      Routes now redirect to the throttled public lookup. The carrier
                      backend (trust-link-carrier-profile) is locked to authed callers so
                      the score is not scriptable via the anon key either. */}
                  <Route path="/carrier/:mc" element={<Navigate to="/verify" replace />} />
                  <Route path="/broker/:mc" element={<Navigate to="/verify" replace />} />
                  {/* Public Canadian broker profile removed 2026-06-19 (same free
                      trust-score-by-ID leak as /carrier/:mc and /broker/:mc). The
                      route now redirects to the throttled public lookup. Declared
                      before /brokers/:region so the static "ca" segment still
                      outranks the :region param and SEO region pages are unaffected. */}
                  <Route path="/brokers/ca/:cab_id" element={<Navigate to="/verify" replace />} />
                  {/* Registry claim verification. The claim email (registry-claim-request)
                      links to /registry/claim/verify?token=...&sig=...; ClaimVerifyPage reads
                      token+sig from the query string. The legacy /claim-profile/:token path is
                      kept for back-compat but never carried the query params the page needs. */}
                  <Route path="/registry/claim/verify" element={<Suspense fallback={<PageFallback />}><ClaimVerifyPage /></Suspense>} />
                  <Route path="/claim-profile/:token" element={<Suspense fallback={<PageFallback />}><ClaimVerifyPage /></Suspense>} />
                  <Route path="/receipt/:receipt_token" element={<Suspense fallback={<PageFallback />}><TrustLinkReceiptPage /></Suspense>} />
                  {/* Public broker package viewer (share-token auth, no JWT). */}
                  <Route path="/pkg/:token" element={<Suspense fallback={<PageFallback />}><PublicPackageView /></Suspense>} />

                  {/* Public carrier signup + invite landing (dash-v2) */}
                  <Route path="/carriers/signup" element={<Suspense fallback={<PageFallback />}><CarrierSignupPage /></Suspense>} />
                  <Route path="/brokers/signup" element={<Suspense fallback={<PageFallback />}><BrokerSignupPage /></Suspense>} />
                  <Route path="/carriers/invite/preview" element={<CarrierInvitePreviewPage />} />
                  <Route path="/carriers/invite/:token" element={<CarrierInviteLandingPage />} />
                  {/* Programmatic SEO · /carriers/:region and /brokers/:region.
                      Static segments above (signup, invite/*) match first per RR v6
                      ranking. Unknown slugs render <Navigate to="/coverage"/>. */}
                  <Route path="/carriers/:region" element={<StateRegistryPage role="carrier" />} />
                  <Route path="/brokers/:region" element={<StateRegistryPage role="broker" />} />
                  <Route path="/refer/ca-broker/:token" element={<Suspense fallback={<PageFallback />}><CABrokerReferPage /></Suspense>} />
                  <Route path="/tl2/:token" element={<TrustLinkDriverPageV2 />} />

                  {/* Auth */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/logout" element={<LogoutRoute />} />
                  <Route path="/signup" element={<Signup />} />
                  {/* Dedicated team-member account creation. Invite emails link
                      to /login?team_invite=<token>; Login redirects an
                      unauthenticated invitee here. */}
                  <Route path="/join-team" element={<TeamInviteAcceptPage />} />
                  <Route path="/mfa-verify" element={<MfaVerify />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  {/* Native email-confirmation landing. Renders AuthCallback directly
                      (NOT a redirect) so the ?token_hash=…&type=… query survives —
                      AuthCallback redeems it via verifyOtp. */}
                  <Route path="/auth/confirm" element={<AuthCallback />} />

                  {/* Legal & Security */}
                  <Route path="/security" element={<SecurityPage />} />
                  <Route path="/terms" element={<Suspense fallback={<PageFallback />}><TermsPage /></Suspense>} />
                  <Route path="/privacy" element={<Suspense fallback={<PageFallback />}><PrivacyPage /></Suspense>} />
                  <Route path="/api-terms" element={<Suspense fallback={<PageFallback />}><ApiTermsPage /></Suspense>} />
                  <Route path="/cookies" element={<Suspense fallback={<PageFallback />}><CookiePolicyPage /></Suspense>} />

                  {/* Trust · status (changelog hidden 2026-06-25 — stale + over-shared internal/security detail; redirect to status) */}
                  <Route path="/status" element={<Suspense fallback={<PageFallback />}><StatusPage /></Suspense>} />
                  <Route path="/changelog" element={<Navigate to="/status" replace />} />

                  {/* Redirects */}
                  <Route path="/api-docs" element={<Navigate to="/docs" replace />} />
                  <Route path="/docs/api" element={<Navigate to="/docs" replace />} />
                  <Route path="/platform" element={<Navigate to="/dashboard/admin" replace />} />
                  <Route path="/sign-in" element={<Navigate to="/login" replace />} />
                  <Route path="/auth" element={<AuthAliasForwarder />} />
                  <Route path="/logout" element={<Navigate to="/login" replace />} />

                  {/* Mobile (carrier/owner-operator) · auth-gated */}
                  <Route path="/m" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageFallback />}><MobileLayout /></Suspense>
                    </ProtectedRoute>
                  }>
                    {/* Mobile home is Alerts: the load feed is a Phase-2 surface (gated
                        below), so it can't be the landing tab. */}
                    <Route index element={<Navigate to="/m/alerts" replace />} />
                    <Route path="loads" element={<Suspense fallback={<PageFallback />}><PhaseGate feature="load_board" denyMessage="Load Board isn't active on your plan yet."><MobileLoadFeed /></PhaseGate></Suspense>} />
                    <Route path="profile" element={<Suspense fallback={<PageFallback />}><MobileProfile /></Suspense>} />
                    <Route path="alerts" element={<Suspense fallback={<PageFallback />}><MobileAlerts /></Suspense>} />
                  </Route>

                  {/* Legacy /dashboard redirects to the new /dash. Dash v2
                      is now live; keeping /dashboard reachable so existing
                      bookmarks, login redirects, and email links all land
                      on the real dashboard. Specific legacy sub-paths map
                      to the closest dash-v2 equivalent. */}
                  <Route path="/dashboard" element={<Navigate to="/dash" replace />} />
                  <Route path="/dashboard/onboarding" element={<Navigate to="/dash/onboarding" replace />} />
                  <Route path="/dashboard/carriers" element={<Navigate to="/dash/carriers/lookup" replace />} />
                  <Route path="/dashboard/intelligence/carrier-lookup" element={<Navigate to="/dash/carriers/lookup" replace />} />
                  <Route path="/dashboard/inbox" element={<Navigate to="/dash/email/ai" replace />} />
                  <Route path="/dashboard/email-sequences" element={<Navigate to="/dash/email/follow-ups" replace />} />
                  <Route path="/dashboard/email-templates" element={<Navigate to="/dash/email/follow-ups" replace />} />
                  <Route path="/dashboard/settings/templates" element={<Navigate to="/dash/email/follow-ups" replace />} />
                  <Route path="/dashboard/trust-links" element={<Navigate to="/dash/trust-links" replace />} />
                  <Route path="/dashboard/settings" element={<Navigate to="/dash/settings" replace />} />
                  <Route path="/dashboard/settings/billing" element={<Navigate to="/dash/settings/billing" replace />} />
                  <Route path="/dashboard/settings/team" element={<Navigate to="/dash/settings/team" replace />} />
                  <Route path="/dashboard/billing" element={<BillingAliasForwarder />} />
                  <Route path="/dashboard/developer" element={<Navigate to="/dash/developer" replace />} />
                  <Route path="/dashboard/settings/usage" element={<Navigate to="/dash/settings/usage" replace />} />
                  <Route path="/dashboard/admin" element={<Navigate to="/dash/admin" replace />} />
                  <Route path="/dashboard/tracking" element={<Navigate to="/dash/fleet/tracking" replace />} />
                  <Route path="/dashboard/qa-testing" element={<Navigate to="/dash/admin/qa" replace />} />
                  <Route path="/dash/qa-testing" element={<Navigate to="/dash/admin/qa" replace />} />
                  {/* Error monitor / flagged carriers: both redirect to the Command
                      Center triage kanban until standalone pages are built. Sentry
                      errors now land in qa_findings and surface there. */}
                  <Route path="/dash/error-monitor" element={<Navigate to="/dash/admin/qa#triage" replace />} />
                  <Route path="/dashboard/error-monitor" element={<Navigate to="/dash/admin/qa#triage" replace />} />
                  <Route path="/dash/flagged-agents" element={<Navigate to="/dash/admin/qa#triage" replace />} />
                  <Route path="/dashboard/flagged-agents" element={<Navigate to="/dash/admin/qa#triage" replace />} />
                  {/* Intuitive but non-canonical paths a user might type or a stale
                      link might point to. Map to the real pages. */}
                  <Route path="/dash/trust-score" element={<Navigate to="/dash/carriers/my-score" replace />} />
                  <Route path="/dashboard/trust-score" element={<Navigate to="/dash/carriers/my-score" replace />} />
                  <Route path="/dash/account" element={<Navigate to="/dash/settings" replace />} />
                  <Route path="/dashboard/account" element={<Navigate to="/dash/settings" replace />} />
                  {/* Stale links from earlier nav layouts. /dash/verifications was the
                      old name for the lookup page; /dash/carriers (no /lookup suffix)
                      collided with the new partner-centric IA. */}
                  <Route path="/dash/verifications" element={<Navigate to="/dash/verify" replace />} />
                  <Route path="/dash/carriers" element={<Navigate to="/dash/partners" replace />} />
                  {/* Catch-all for any other /dashboard/* path. Previously
                      dropped the subpath and landed every legacy URL on
                      /dash (the dispatcher home). That made any unmapped
                      /dashboard/X URL look like a deep-link bounce to the
                      dashboard, even though the underlying /dash/X route
                      exists. Per Bruno's /cowork audit 2026-05-20.
                      Now preserve the subpath so /dashboard/foo/bar -> /dash/foo/bar
                      and React Router takes over from there. Falls back
                      cleanly to /dash if the resulting /dash path also
                      doesn't exist (NotFound). */}
                  <Route path="/dashboard/*" element={<DashboardPathForwarder />} />

                  {/* Dash v2 (in progress). Mounts under /dash to keep v1 and v2
                      isolated while the rebuild is happening. Moves to /dashboard
                      once v2 is proven and the legacy routes above are deleted.
                      Routes without a dedicated page yet land on SystemStatePage
                      which shows a Phase 1 "coming soon" variant. */}
                  {/* Post-signup role picker: shown to new users before onboarding.
                      Not wrapped in ProtectedRoute — the component calls
                      getSession() directly to avoid a race with AuthProvider's
                      async onAuthStateChange handler. */}
                  <Route path="/signup/choose-role" element={<SignupRolePicker />} />
                  {/* RequireLogisticsRole: picking a role is mandatory before any
                      wizard or dashboard surface renders — it's what starts the
                      14-day trial (see the component for the full note). The
                      wizards sit OUTSIDE DashV2Layout, so they need the wrapper
                      individually or a role-less user can deep-link straight
                      into the dispatcher wizard and finish it with no role. */}
                  <Route path="/dash/onboarding" element={
                    <ProtectedRoute><RequireLogisticsRole><DispatcherOnboardingPage /></RequireLogisticsRole></ProtectedRoute>
                  } />
                  <Route path="/dash/onboarding/broker" element={
                    <ProtectedRoute><RequireLogisticsRole><BrokerOnboardingPage /></RequireLogisticsRole></ProtectedRoute>
                  } />
                  <Route path="/dash/onboarding/carrier" element={
                    <ProtectedRoute><RequireLogisticsRole><CarrierOnboardingPage /></RequireLogisticsRole></ProtectedRoute>
                  } />
                  <Route path="/dash" element={<ProtectedRoute><RequireLogisticsRole><DashV2Layout /></RequireLogisticsRole></ProtectedRoute>}>
                    <Route index element={<HomeGate />} />
                    {/* The wall for an expired 14-day trial. DashV2Layout
                        redirects every other /dash route here while the
                        company's posture is trial_expired; this route itself is
                        allowlisted there so the redirect can't loop. */}
                    <Route path="trial-ended" element={<TrialEndedPage />} />
                    <Route path="home" element={<AuditModeRedirect><DispatcherHomePage /></AuditModeRedirect>} />
                    <Route path="verify" element={<VerificationDashboardPage />} />
                    <Route path="verify/swipe" element={
                      <PhaseGate feature="carrier_vetting_swipe" denyMessage="Swipe vetting isn't active on your plan yet."><CarrierVettingSwipePage /></PhaseGate>
                    } />
                    <Route path="bulk-verify" element={<BulkVerifyPage />} />
                    <Route path="carriers/lookup" element={
                      <RoleGate allow={['dispatcher', 'broker']} denyMessage="Carrier lookup is for dispatcher and broker accounts. Carriers vet brokers from Broker Lookup."><CarrierLookupPage /></RoleGate>
                    } />
                    <Route path="carriers/my-score" element={
                      // Held in UNRELEASED_FEATURES until the full-breakdown rebuild
                      // ships (founder 07-14); admins dogfood via PhaseGate allowAdmin.
                      <PhaseGate feature="my_trust_score" denyMessage="My Trust Score is getting a rebuild and will be back shortly.">
                        <RoleGate allow={['carrier', 'broker']} denyMessage="Trust scores aren't available for dispatcher accounts yet."><MyTrustScorePage /></RoleGate>
                      </PhaseGate>
                    } />

                    {/* Ownership-verification "email me a link" landing page. Matches
                        the link vernot's ownership-claim-request emails:
                        ${APP_URL}/dash/trust/verify-ownership?request_id=...&token=... */}
                    <Route path="trust/verify-ownership" element={<VerifyOwnershipEmailPage />} />
                    <Route path="docs/scoring" element={<ScoringMethodologyPage />} />
                    <Route path="carriers/:mc" element={<CarrierDetailPage />} />
                    <Route path="brokers/lookup" element={<BrokerLookupPage />} />
                    {/* CA broker directory + detail · static path BEFORE
                        :cab_id so /dash/brokers/ca renders the list, and
                        /dash/brokers/ca/:cab_id renders the detail. Both
                        precede :mc so CAB-XX-XXXXXXX isn't swallowed by US. */}
                    <Route path="brokers/ca" element={<CABrokerListPage />} />
                    {/* Claim magic-link landing. MUST precede :cab_id so the
                        literal /claim/verify path isn't swallowed as a CAB id.
                        Linked from the broker-claim-request email
                        (APP_URL + /dash/brokers/ca/claim/verify?token=...). */}
                    <Route path="brokers/ca/claim/verify" element={
                      <CabIdGate verifyHref="/dash/brokers/ca">
                        <Suspense fallback={<PageFallback />}><CABrokerClaimVerifyPage /></Suspense>
                      </CabIdGate>
                    } />
                    {/* Broker-only "Claim your CAB #" home. Static path BEFORE
                        :cab_id so "claim" isn't swallowed by the detail route.
                        Gated by CAB_ID_ENABLED — the program shows "Coming soon"
                        until the proof gate ships (CA broker lookup is unaffected). */}
                    <Route path="brokers/ca/claim" element={
                      <CabIdGate verifyHref="/dash/brokers/ca">
                        {/* Brokers see this; platform admins bypass via RoleGate's
                            allowAdmin default, so 'admin' is not listed (it is not a
                            GateRole and would be a type error). */}
                        <RoleGate allow={['broker']} denyMessage="Claim your CAB # is for broker accounts.">
                          <OrgAdminGate denyMessage="Claim your CAB # is managed by your organization's admin."><ClaimCABPage /></OrgAdminGate>
                        </RoleGate>
                      </CabIdGate>
                    } />
                    <Route path="brokers/ca/:cab_id" element={<CABrokerDetailPage />} />
                    <Route path="brokers/onboarding/ca/documents" element={<Suspense fallback={<PageFallback />}><CABrokerDocumentsPage /></Suspense>} />
                    <Route path="brokers/:mc" element={<BrokerDetailPage />} />
                    <Route path="partners" element={<MyPartnersPage />} />
                    <Route path="partners/alerts" element={<PartnerAlertsPage />} />
                    <Route path="alerts/:alertId" element={<FocusedAlertPage />} />
                    <Route path="packages/inbox" element={<PhaseGate feature="package_scanner" denyMessage="The packet scanner isn't active on your plan yet."><PackageInboxPage /></PhaseGate>} />
                    <Route path="packages/scanner" element={<PhaseGate feature="package_scanner" denyMessage="The packet scanner isn't active on your plan yet."><PackageScannerPage /></PhaseGate>} />
                    <Route path="packages/scan/:scanId" element={<PhaseGate feature="package_scanner" denyMessage="The packet scanner isn't active on your plan yet."><ScanDetailPage /></PhaseGate>} />
                    <Route path="packages/review-queue" element={<PhaseGate feature="scan_agent_layer" denyMessage="The agent review queue isn't active on your plan yet."><ReviewQueuePage /></PhaseGate>} />
                    {/* The scan API's dashboard_url + reviewer emails link /dash/scans/:id — alias it. */}
                    <Route path="scans/:scanId" element={<ScanShortRedirect />} />
                    {/* Records routes are NOT scanner-gated (founder ruling 2026-08-07):
                        Save-to-vault ships to every lookup user, so anyone who can mint a
                        record must be able to view it. Scanner surfaces above stay gated. */}
                    <Route path="packages/records" element={<ComplianceVaultPage />} />
                    <Route path="packages/records/:reportNo" element={<VaultRecordPage />} />
                    <Route path="packages/builder" element={<PhaseGate feature="package_builder" denyMessage="The packet builder isn't active on your plan yet."><PackageBuilderWizard /></PhaseGate>} />
                    <Route path="broker/package" element={<BrokerPackagePage />} />
                    {/* Both /dash/developers and /dash/developer render the
                        rebuilt tabbed developer portal (Keys / Samples /
                        Webhooks / Docs). The legacy MCP-only landing under
                        developer/mcp now redirects to the unified in-dash MCP
                        hub (/dash/mcp) so there's a single connected MCP
                        surface instead of a disconnected sub-page. Removed
                        AuditModeRedirect wrappers: the dev portal should be
                        reachable for all authenticated tenants regardless of
                        dashboard_mode, since the API surface is what's being
                        documented, not the dashboard scope. */}
                    <Route path="developers" element={<DevelopersPage />} />
                    <Route path="developer" element={<DevelopersPage />} />
                    <Route path="developer/mcp" element={<Navigate to="/dash/mcp" replace />} />
                    <Route path="inbox" element={<Navigate to="/dash/email/ai" replace />} />
                    <Route path="email" element={<Navigate to="/dash/email/ai" replace />} />
                    <Route path="email/observer" element={
                      <PhaseGate feature="email_ai_observer" denyMessage="Observer mode isn't active on your account yet."><EmailAIObserverPage /></PhaseGate>
                    } />
                    <Route path="email/follow-ups" element={
                      <PhaseGate feature="email_templates" denyMessage="Email templates aren't active on your plan yet."><FollowUpsPage /></PhaseGate>
                    } />
                    <Route path="email/sequences" element={
                      <PhaseGate feature="email_templates" denyMessage="Email sequences aren't active on your plan yet."><EmailSequencesPage /></PhaseGate>
                    } />
                    <Route path="sequences" element={<Navigate to="/dash/email/sequences" replace />} />
                    <Route path="email/templates" element={
                      <PhaseGate feature="email_templates" denyMessage="Email templates aren't active on your plan yet."><EmailTemplatesPage /></PhaseGate>
                    } />
                    <Route path="email/ai" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><EmailAIPage /></PhaseGate>
                    } />
                    <Route path="email/scanner" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><EmailScannerPage /></PhaseGate>
                    } />
                    <Route path="email/schedule" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><EmailSchedulePage /></PhaseGate>
                    } />
                    <Route path="email/playbooks" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><EmailPlaybooksPage /></PhaseGate>
                    } />
                    <Route path="email/audit" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><EmailAuditPage /></PhaseGate>
                    } />
                    <Route path="email/integrations" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><EmailIntegrationsPage /></PhaseGate>
                    } />
                    <Route path="email/sent" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><SentPage /></PhaseGate>
                    } />
                    <Route path="email/kb" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><KnowledgeBasePage /></PhaseGate>
                    } />
                    <Route path="email/automations" element={
                      <PhaseGate feature="automation_rules" denyMessage="Automation rules aren't active on your plan yet."><AutomationRulesPage /></PhaseGate>
                    } />
                    <Route path="trust-links" element={
                      <PhaseGate feature="trust_links" denyMessage="Trust Links aren't active on your plan yet."><TrustLinksPage /></PhaseGate>
                    } />
                    <Route path="disputes/scores" element={
                      <PhaseGate feature="score_disputes" denyMessage="Score disputes aren't active on your plan yet."><ScoreDisputesPage /></PhaseGate>
                    } />
                    <Route path="fleet/tracking" element={
                      <PhaseGate feature="fleet_tracking" denyMessage="Fleet tracking isn't active on your plan yet."><FleetTrackingPage /></PhaseGate>
                    } />
                    <Route path="loads" element={
                      <PhaseGate feature="load_board" denyMessage="Load Board isn't active on your plan yet."><DispatcherLoadBoardPage /></PhaseGate>
                    } />
                    <Route path="loads/:tenderId" element={
                      <PhaseGate feature="load_board" denyMessage="Load Board isn't active on your plan yet."><LoadTrustTimelinePage /></PhaseGate>
                    } />
                    <Route path="documents" element={
                      <PhaseGate feature="documents" denyMessage="Document management isn't active on your plan yet."><DocumentsPage /></PhaseGate>
                    } />
                    <Route path="documents/:id" element={
                      <PhaseGate feature="documents" denyMessage="Document management isn't active on your plan yet."><DocumentReportPage /></PhaseGate>
                    } />
                    <Route path="send-package" element={<ProtectedRoute requiredRole="admin"><SendPackagePage /></ProtectedRoute>} />
                    <Route path="settings" element={<SettingsHubPageV2 />} />
                    <Route path="settings/billing" element={<BillingPageV2 />} />
                    <Route path="settings/usage" element={<UsagePageV2 />} />
                    <Route path="referrals" element={<ReferralsPage />} />
                    <Route path="settings/team" element={<TeamPageV2 />} />
                    <Route path="settings/sandbox" element={<SandboxSettingsPage />} />
                    <Route path="settings/notifications" element={<NotificationSettingsPage />} />
                    <Route path="feedback" element={<FeedbackPage />} />
                    <Route path="settings/email" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><EmailSettingsPage /></PhaseGate>
                    } />
                    <Route path="settings/verification" element={<VerificationSettingsPage />} />
                    <Route path="settings/scanner" element={
                      <PhaseGate feature="package_scanner" denyMessage="The packet scanner isn't active on your plan yet."><ScannerSettingsPage /></PhaseGate>
                    } />
                    <Route path="settings/scan-api" element={
                      <PhaseGate feature="scan_api" denyMessage="The Scan API isn't active on your plan yet."><ScanApiSettingsPage /></PhaseGate>
                    } />
                    <Route path="settings/locale" element={<LocaleSettingsPage />} />
                    <Route path="settings/documents" element={
                      <PhaseGate feature="documents" denyMessage="Document management isn't active on your plan yet."><DocumentSettingsPage /></PhaseGate>
                    } />
                    <Route path="settings/deals" element={
                      <PhaseGate feature="deal_engine" denyMessage="The deal engine isn't active on your plan yet."><DealSettingsPage /></PhaseGate>
                    } />
                    <Route path="settings/trust" element={
                      <PhaseGate feature="trust_links" denyMessage="Trust & visibility settings aren't active on your plan yet."><TrustSettingsPage /></PhaseGate>
                    } />
                    <Route path="settings/security" element={<SecuritySettingsPage />} />
                    <Route path="settings/sign-anchor" element={
                      <PhaseGate feature="trust_signature" denyMessage="Trust Signature isn't active on your plan yet."><SignAnchorSettingsPage /></PhaseGate>
                    } />
                    <Route path="settings/rates" element={
                      <PhaseGate feature="rate_intelligence" denyMessage="Rate intelligence isn't active on your plan yet."><RateSettingsPage /></PhaseGate>
                    } />
                    <Route path="settings/integrations" element={
                      <PhaseGate feature="invoices" denyMessage="Integrations aren't active on your plan yet."><IntegrationSettingsPage /></PhaseGate>
                    } />
                    {/* Primary MCP hub — connections + usage + recent activity. */}
                    <Route path="mcp" element={<McpConnectionsPage />} />
                    {/* Backward-compat alias for any deep links saved before MCP
                        got promoted out of /settings. */}
                    <Route path="settings/mcp" element={<Navigate to="/dash/mcp" replace />} />
                    <Route path="invoices" element={
                      <PhaseGate feature="invoices" denyMessage="Invoices aren't active on your plan yet."><InvoicesPageV2 /></PhaseGate>
                    } />
                    <Route path="invoices/settings" element={
                      <PhaseGate feature="invoices" denyMessage="Invoice settings aren't active on your plan yet."><InvoiceSettingsPage /></PhaseGate>
                    } />
                    <Route path="admin" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="hub-cockpit"><AdminCockpitHubPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    {/* Consolidated into Growth (2026-07-11). */}
                    <Route path="admin/traction" element={<Navigate to="/dash/admin/growth" replace />} />
                    <Route path="admin/briefing" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="briefing"><FounderBriefingPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    {/* ELD device lookup. Admin-only at the route AND in the RPC
                        (eld_device_lookup re-checks is_platform_admin and honours
                        system_flags.eld_lookup_enabled), so the gate holds even if
                        someone calls the RPC directly. */}
                    <Route path="admin/eld" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="eld"><AdminEldLookupPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    {/* Webhooks & Shares — read-only delivery health for the
                        trust-bus webhook pipeline + packet share activity. The
                        admin-webhooks-overview fn re-checks is_platform_admin
                        server-side. */}
                    <Route path="admin/webhooks" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="webhooks"><AdminWebhooksPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/vc" element={<Navigate to="/dash/admin/revenue" replace />} />
                    <Route path="admin/accelerator" element={<Navigate to="/dash/admin/growth" replace />} />
                    {/* Cohesive admin hub lifted to BE the admin home: cockpit at
                        /dash/admin + section/detail/settings pages as siblings. */}
                    <Route path="admin/growth" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="hub-growth"><AdminHubGrowthPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/revenue" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="hub-revenue"><AdminHubRevenuePage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/verification" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="hub-verification"><AdminHubVerificationPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/geography" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="hub-geography"><AdminHubGeographyPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/health" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="hub-health"><AdminHubHealthPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/tenants" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="hub-tenants"><AdminHubTenantsPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/tenant/:id" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="hub-tenant"><AdminHubTenantDetailPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/settings" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="hub-settings"><AdminHubSettingsPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    {/* Back-compat: forward any retired /dash/admin/hub/* deep link
                        to the same subpath under /dash/admin. */}
                    <Route path="admin/hub/*" element={<AdminHubLegacyRedirect />} />
                    <Route path="admin/qa" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="qa"><QACommandCenterPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    {/* QA cockpit lives at /qa-cockpit; /cockpit was a naming trap
                        vs the real admin cockpit at /dash/admin. Redirect the old path. */}
                    <Route path="admin/qa-cockpit" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="qa-cockpit"><QACockpitPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/cockpit" element={<Navigate to="/dash/admin/qa-cockpit" replace />} />
                    <Route path="admin/trust-safety" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="trust-safety"><TrustSafetyAdminPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/status" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="status-admin"><StatusAdminPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/feedback" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="feedback-inbox"><FeedbackInboxPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/pipelines" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="pipelines"><PipelinesPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/roadmap" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="roadmap"><AdminRoadmapPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/referrals" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="referrals"><AdminReferralsPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    {/* Retired 2026-07-11: per-tenant email freeze moved to the
                        tenant detail page (Configuration tab); the Freeze-ALL
                        cluster moved to Emergency Controls. */}
                    <Route path="admin/tenant-controls" element={<Navigate to="/dash/admin/tenants" replace />} />
                    <Route path="admin/emergency-controls" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="emergency-controls"><AdminEmergencyControlsPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/embed-badge" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="embed-badge"><EmbedBadgeTesterPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/email-ai-tester" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="email-ai-tester"><EmailAITesterPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/intakes" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="intakes"><IntakeMonitorPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/doc-review" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="doc-review"><DocReviewQueuePage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/ca-broker-claims" element={
                      <ProtectedRoute requiredRole="admin">
                        <CabIdGate verifyHref="/dash/brokers/ca">
                          <AdminRouteBoundary routeId="ca-broker-claims"><CABrokerClaimsPage /></AdminRouteBoundary>
                        </CabIdGate>
                      </ProtectedRoute>
                    } />
                    <Route path="admin/doc-bundle" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="doc-bundle"><DocBundlePage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/broker-package-ops" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="broker-package-ops"><AdminBrokerPackageOpsPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    {/* Duplicate of the hub Health page; consolidated 2026-07-11. */}
                    <Route path="admin/ingest-health" element={<Navigate to="/dash/admin/health" replace />} />
                    <Route path="admin/package-scanner-v2" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="package-scanner-v2"><AdminPackageScannerV2Page /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    {/* Dead phase-blocked stub; consolidated 2026-07-11. */}
                    <Route path="intelligence/fraud" element={<Navigate to="/dash/admin" replace />} />
                    <Route path="404" element={<SystemStatePage variant="not-found" />} />
                    <Route path="403" element={<SystemStatePage variant="phase-blocked" />} />
                    <Route path="network-error" element={<SystemStatePage variant="network-error" />} />
                    <Route path="session-expired" element={<SystemStatePage variant="session-expired" />} />
                    <Route path="*" element={<SystemStatePage variant="not-found" />} />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </CinematicRouteFrame>
              <CinematicTransitionLayer />
              </CinematicTransitionProvider>
              </SiteThemeProvider>
            </BrowserRouter>
          </TooltipProvider>
        </VerticalProvider>
      </CompanyModeProvider>
    </AuthProvider>
    </GlobalErrorProvider>
  </QueryClientProvider>
  </ErrorBoundary>
  </HelmetProvider>
);

export default App;
